import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { captureSmartTablePresetCreated } from "./analytics";
import {
  CreateCustomPresetRequest,
  CreateCustomPresetResponse,
  SmartTableDetail,
  unwrapSmartTableEntityPayload,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { routePaths } from "@/utils/routePaths";
import { displayToast } from "@/utils/toast";

export const useCreateCustomPreset = ({ openInNewTab = false } = {}) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<SmartTableDetail, unknown, CreateCustomPresetRequest>({
    mutationFn: async payload => {
      const response = await fetch(ENDPOINTS.smart_table_custom_preset(wid), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });
      const responsePayload = (await handleResponse(response)) as CreateCustomPresetResponse;
      return unwrapSmartTableEntityPayload<SmartTableDetail>(responsePayload, "smart_table");
    },
    onSuccess: (smartTable, payload) => {
      queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, "list"] });

      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: smartTable.id,
        presetType: "custom",
        promptRegistryId: payload.prompt_registry_id,
        promptVersionNumber: payload.prompt_version_number,
      });

      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["folder-entities", workspaceId] });
        const url = routePaths.workspaceSmartTable(workspaceId, smartTable.id);
        if (openInNewTab) {
          window.open(url, "_blank");
        } else {
          navigate(url);
        }
      }

      displayToast("Custom table created", ToastType.success);
    },
  });
};
